import { useState, JSX } from 'react';

import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CircleRoundedIcon from '@mui/icons-material/Circle';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import PropTypes from 'prop-types';

import { useIntl, FormattedMessage } from '@zenbusiness/application-commons-intl';
import IconButton from '@zenbusiness/zui/IconButton';
import InputAdornment from '@zenbusiness/zui/InputAdornment';
import Stack from '@zenbusiness/zui/Stack';
import superstyled from '@zenbusiness/zui/styles/superstyled';
import Text from '@zenbusiness/zui/Text';

import Field from '#client/components/Field';

import {
  ERROR_KEY_TO_TERM_MAP,
  mapValidationsStatus,
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH
} from './validations';

const commonIconStyles = {
  fontSize: 16
};

const StyledCheckIcon = superstyled({
  name: 'ValidationIcon',
  slot: 'Check',
  tag: CheckCircleRoundedIcon,
  propsAdded: {
    color: 'success'
  },
  styles: commonIconStyles
});

const StyledCancelIcon = superstyled({
  name: 'ValidationIcon',
  slot: 'Cancel',
  tag: CancelRoundedIcon,
  propsAdded: {
    color: 'error'
  },
  styles: commonIconStyles
});

const StyledCircleIcon = superstyled({
  name: 'ValidationIcon',
  slot: 'Circle',
  tag: CircleRoundedIcon,
  propsAdded: {
    color: 'disabled'
  },
  styles: commonIconStyles
});

/**
 * ValidationIcon component displays an icon based on the validation status.
 *
 * @param {object} props - The component props.
 * @param {boolean} props.hasPassed - Indicates if validation has passed.
 * @param {boolean} props.hasError - Indicates if there is an error in validation.
 * @returns {JSX.Element} The icon representing the validation status.
 */
const ValidationIcon = ({ hasPassed = false, hasError = false }) => {
  if (hasPassed) {
    return <StyledCheckIcon />;
  }

  if (hasError) {
    return <StyledCancelIcon />;
  }

  return <StyledCircleIcon />;
};

ValidationIcon.propTypes = {
  hasPassed: PropTypes.bool,
  hasError: PropTypes.bool
};
function PasswordField(props) {
  const [isVisible, setIsVisible] = useState(false);
  const { formatMessage } = useIntl();
  const { value, error, disabled } = props;

  const toggleVisibility = () => {
    setIsVisible((prev) => !prev);
  };

  return (
    <>
      <Field
        {...props}
        InputProps={{
          inputProps: {
            'data-testid': 'password-input',
            autoComplete: 'new-password',
            maxLength: PASSWORD_MAX_LENGTH,
            minLength: PASSWORD_MIN_LENGTH
          },
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label={formatMessage({
                  id: 'shopping.page.checkout.account.form.field.password.icon.label'
                })}
                clickstreamProps={{
                  elementName: 'password_visibility_toggle',
                  elementValue: isVisible ? 'visible' : 'hidden',
                  elementAsset: isVisible ? 'VisibilityOffRoundedIcon' : 'VisibilityRoundedIcon'
                }}
                data-testid={`password-visibility-toggle-${isVisible ? 'visible' : 'hidden'}`}
                disableRipple
                edge="end"
                label={formatMessage({
                  id: 'shopping.page.checkout.account.form.field.password.icon.label'
                })}
                onClick={toggleVisibility}
              >
                {isVisible ? <VisibilityOffRoundedIcon /> : <VisibilityRoundedIcon />}
              </IconButton>
            </InputAdornment>
          )
        }}
        label={formatMessage({ id: 'shopping.page.checkout.account.form.field.password.label' })}
        sx={{
          // Override the default styles for the password field when disabled
          '& .MuiInputBase-root.Mui-disabled': {
            background: 'none',
            border: 'none'
          },

          '& .MuiInputBase-input.Mui-disabled': {
            px: 0
          },

          '& .MuiInputLabel-root.Mui-disabled': {
            fontSize: 14,
            fontWeight: 600,
            color: 'inherit',
            transform: 'translate(0px, 8px) scale(1)'
          },

          // Hide the helper text section in favor of the custom errors section below
          '& .MuiFormHelperText-root': {
            display: 'none'
          }
        }}
        type={isVisible ? 'text' : 'password'}
      />
      {!disabled && (
        <Stack
          bgcolor="background.grey.main"
          borderRadius={1}
          padding={1.5}
          role="alert"
          spacing={1.5}
        >
          <Text size="xs" weight="medium">
            <FormattedMessage id="shopping.page.checkout.account.form.errors.title" />
          </Text>
          {Object.entries(mapValidationsStatus(value)).map(([validationKey, hasPassed]) => (
            <Text
              key={validationKey}
              data-testid={`password-validation-${validationKey}`}
              size="xs"
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <ValidationIcon hasError={!!error} hasPassed={hasPassed} />
              <FormattedMessage
                id={ERROR_KEY_TO_TERM_MAP[validationKey]}
                values={{
                  minLength: PASSWORD_MIN_LENGTH,
                  maxLength: PASSWORD_MAX_LENGTH
                }}
              />
            </Text>
          ))}
        </Stack>
      )}
    </>
  );
}

PasswordField.propTypes = {
  ...Field.propTypes,
  value: PropTypes.string.isRequired
};

export default PasswordField;
